import './applyApplicationTypePage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const { applicationType, setApplicationType } = useApplyStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      applicationTypeValue: applicationType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<ApplicationType>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const applicationTypeValue = event.target.value;

    setIsInitialSelection(isEmpty(applicationType) && isValidClickEvent(event));

    setApplicationType(applicationTypeValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { applicationTypeValue } = values;

    if (applicationTypeValue) {
      navigate(next());
    }
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page apply-application-type-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applyApplicationType.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applyApplicationType.form.h1}>
                {t('ApplyApplicationTypePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applyApplicationType.form.h2}>
                {t('ApplyApplicationTypePage.subtitle')}
              </h2>

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  name="applicationTypeValue"
                  options={[
                    {
                      label: t('ApplyApplicationTypePage.refinance'),
                      value: ApplicationType.REFINANCE,
                      testId:
                        locator.pages.applyApplicationType.form.radioOption1,
                    },
                    {
                      label: t('ApplyApplicationTypePage.leaseBuyout'),
                      value: ApplicationType.LEASE_BUYOUT,
                      testId:
                        locator.pages.applyApplicationType.form.radioOption2,
                    },
                    {
                      label: t('ApplyApplicationTypePage.purchase'),
                      value: ApplicationType.PURCHASE,
                      testId:
                        locator.pages.applyApplicationType.form.radioOption3,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applyApplicationType.form.radioGroupRoot
                  }
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.applyApplicationType.form.submitButton
                  }
                  disabled={isEmpty(applicationType)}
                  fullWidth
                  submit
                >
                  {t('ApplyApplicationTypePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applyApplicationType.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplyApplicationTypePage';
